import React, { useState } from 'react';
import { SelectionDialog } from 'components';
import { AddTemplateIcon } from 'assets';
import {
    useNotify,
    useScorecardTemplates,
    useCreateScorecardFromTemplate,
    useGetAllowedValues
} from 'hooks';

interface CreateFromTemplateProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CreateFromTemplate: React.FC<CreateFromTemplateProps> = ({
    isOpen,
    onClose
}) => {
    const notify = useNotify();
    const { data: templates, isLoading: templatesLoading } = useScorecardTemplates();
    const { data } = useGetAllowedValues();
    const sites = data?.sites;
    const createFromTemplate = useCreateScorecardFromTemplate();

    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [isSiteSelectionOpen, setIsSiteSelectionOpen] = useState(false);

    const handleTemplateSelect = (templateId: number) => {
        setSelectedTemplateId(templateId);
        setIsSiteSelectionOpen(true);
    };

    const handleSitesSelect = (siteIds: number[]) => {
        if (selectedTemplateId) {
            createFromTemplate.mutate({
                siteIds,
                templateId: selectedTemplateId
            }, {
                onSuccess: () => {
                    notify.success('Scorecard created from template successfully');
                    onClose();
                    setSelectedTemplateId(null);
                    setIsSiteSelectionOpen(false);
                },
                onError: (error: any) => {
                    notify.error(`Failed to create scorecard from template: ${error.message}`);
                }
            });
        }
    };

    const handleSiteSelectionClose = () => {
        setIsSiteSelectionOpen(false);
        setSelectedTemplateId(null);
    };

    const handleBackButtonClick = () => {
        setIsSiteSelectionOpen(false);
        setSelectedTemplateId(null);
    };

    return (
        <>
            {/* Step 1: Template Selection */}
            <SelectionDialog
                options={(templates || []).map(template => ({
                    id: template.id,
                    name: template.name
                }))}
                isDialogOpen={isOpen && !isSiteSelectionOpen}
                onDialogClose={onClose}
                onSingleSelect={handleTemplateSelect}
                multiSelect={false}
                dialogTitle="Step 1/2"
                mainTitle="Select Template"
                searchPlaceholder="Search Templates"
                submitButtonText="Next"
                cancelButtonText="Cancel"
                isSubmitting={templatesLoading}
                customOptionIcon={AddTemplateIcon}
            />

            {/* Step 2: Site Selection */}
            <SelectionDialog
                options={(sites || []).map(site => ({
                    id: site.siteId,
                    name: site.siteName
                }))}
                isDialogOpen={isSiteSelectionOpen}
                onDialogClose={handleSiteSelectionClose}
                onMultiSelect={handleSitesSelect}
                multiSelect
                dialogTitle="Step 2/2"
                mainTitle="Assign Template to Sites"
                submitButtonText="Confirm"
                cancelButtonText="Back"
                onCancel={handleBackButtonClick}
                isSubmitting={createFromTemplate.isLoading}
            />
        </>
    );
};
